import { QueryClient } from '@tanstack/react-query';

/**
 * React Query Client Configuration
 * 
 * Optimized for LinkLoad security scanning platform with:
 * - Stale-while-revalidate caching strategy
 * - Minimal refetching to reduce server load
 * - Retry logic for failed requests
 * - Keep previous data during refetch for smooth UX
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale-while-revalidate pattern
      // Data is considered fresh for 30 seconds
      staleTime: 30000, // 30 seconds
      
      // Cache data for 5 minutes after becoming stale
      cacheTime: 300000, // 5 minutes
      
      // Don't refetch on window focus (annoying for users)
      refetchOnWindowFocus: false,
      
      // Don't refetch on reconnect (we'll handle this manually if needed)
      refetchOnReconnect: false,
      
      // Don't refetch on mount if data is fresh
      refetchOnMount: false,
      
      // Retry failed requests once with exponential backoff
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Keep previous data while fetching new data (smooth UX)
      keepPreviousData: true,
      
      // Suspense mode disabled (we'll handle loading states manually)
      suspense: false,
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

/**
 * Query Key Factory
 * 
 * Provides consistent query keys across the application for:
 * - Better cache management
 * - Easier invalidation
 * - Type safety
 * - Debugging
 */
export const queryKeys = {
  // Scans query keys
  scans: {
    all: ['scans'],
    lists: () => [...queryKeys.scans.all, 'list'],
    list: (filters) => [...queryKeys.scans.lists(), filters],
    details: () => [...queryKeys.scans.all, 'detail'],
    detail: (id) => [...queryKeys.scans.details(), id],
    status: (id) => [...queryKeys.scans.all, 'status', id],
  },
  
  // Vulnerabilities query keys
  vulnerabilities: {
    all: ['vulnerabilities'],
    lists: () => [...queryKeys.vulnerabilities.all, 'list'],
    list: (scanId) => [...queryKeys.vulnerabilities.lists(), scanId],
    detail: (id) => [...queryKeys.vulnerabilities.all, 'detail', id],
  },
  
  // User query keys
  user: {
    all: ['user'],
    profile: () => [...queryKeys.user.all, 'profile'],
    settings: () => [...queryKeys.user.all, 'settings'],
  },
  
  // Dashboard query keys
  dashboard: {
    all: ['dashboard'],
    stats: () => [...queryKeys.dashboard.all, 'stats'],
    recentScans: () => [...queryKeys.dashboard.all, 'recent-scans'],
  },
};

/**
 * Query Client Utilities
 */
export const queryUtils = {
  /**
   * Invalidate all scan queries
   */
  invalidateScans: () => {
    return queryClient.invalidateQueries(queryKeys.scans.all);
  },
  
  /**
   * Invalidate specific scan detail
   */
  invalidateScanDetail: (scanId) => {
    return queryClient.invalidateQueries(queryKeys.scans.detail(scanId));
  },
  
  /**
   * Invalidate scan list
   */
  invalidateScanList: () => {
    return queryClient.invalidateQueries(queryKeys.scans.lists());
  },
  
  /**
   * Prefetch scan details
   */
  prefetchScanDetails: (scanId, fetcher) => {
    return queryClient.prefetchQuery({
      queryKey: queryKeys.scans.detail(scanId),
      queryFn: fetcher,
      staleTime: 60000, // 1 minute
    });
  },
  
  /**
   * Set scan data in cache
   */
  setScanData: (scanId, data) => {
    queryClient.setQueryData(queryKeys.scans.detail(scanId), data);
  },
  
  /**
   * Get scan data from cache
   */
  getScanData: (scanId) => {
    return queryClient.getQueryData(queryKeys.scans.detail(scanId));
  },
  
  /**
   * Clear all caches
   */
  clearAll: () => {
    return queryClient.clear();
  },
};

export default queryClient;

// Made with Bob
